/**
 * How the billing screen writes money, plans and dates. Amounts from the
 * billing API are integer cents in the price's own currency.
 */

/**
 * Format cents in a currency.
 *
 * @param {number} cents - Amount in the currency's minor unit
 * @param {string} [currency='usd'] - ISO 4217 code, any case
 * @returns {string} e.g. "$49.00"
 */
export function formatMoney(cents, currency = 'usd') {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: (currency || 'usd').toUpperCase(),
    }).format((cents ?? 0) / 100)
}

/**
 * The same, with a minus sign in front for money that comes off an invoice.
 *
 * @param {number} cents - A positive amount to take off
 * @param {string} [currency='usd']
 * @returns {string} e.g. "-$9.80"
 */
export function formatDeduction(cents, currency = 'usd') {
    return '-' + formatMoney(Math.abs(cents ?? 0), currency)
}

/**
 * "month" and "year" are what the API calls an interval; people say monthly
 * and annual.
 *
 * @param {'month'|'year'} interval
 * @returns {'monthly'|'annual'}
 */
export function intervalName(interval) {
    return interval === 'year' ? 'annual' : 'monthly'
}

/**
 * A plan's price on one line.
 *
 * @param {{ amount: number, currency: string, interval: string, is_free?: boolean }} plan - A price as the billing API describes it
 * @returns {string} "Free", "$49.00/mo" or "$490.00/yr"
 */
export function priceLabel(plan) {
    if (!plan || plan.is_free || plan.amount === 0) return 'Free'

    const unit = plan.interval === 'year' ? 'yr' : 'mo'
    return `${formatMoney(plan.amount, plan.currency)}/${unit}`
}

/**
 * How much of a limit has been used, as a whole-number percentage that the bar
 * can draw (never above 100). A plan with no limit has nothing to measure.
 *
 * @param {number} usage
 * @param {number|null} limit
 * @returns {number|null}
 */
export function usagePercent(usage, limit) {
    if (limit === null || limit === undefined || limit <= 0) return null

    return Math.min(100, Math.round(((usage ?? 0) / limit) * 100))
}

/**
 * What a discount takes off, in words.
 *
 * @param {{ type: string, percent_off: number|null, amount_off: number|null, currency: string|null }} discount
 * @returns {string} "20% off" or "$10.00 off"
 */
export function discountSize(discount) {
    if (discount.type === 'percent') {
        return `${discount.percent_off}% off`
    }

    return `${formatMoney(discount.amount_off, discount.currency ?? 'usd')} off`
}
